/**
 * AdminTopEarners.tsx
 * 
 * Reusable component for displaying top earners in admin dashboard.
 * Maintains exact visual design from AdminFinancials page.
 */

import React from 'react'
import { Activity } from 'lucide-react'

interface TopEarner {
  userId: string
  userName: string
  revenue: number
  chatbots: number
}

interface TopEarnersProps {
  topEarners: TopEarner[]
}

const AdminTopEarners: React.FC<TopEarnersProps> = ({ topEarners }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Top Earners</h3>
        <Activity className="h-5 w-5 text-gray-400" />
      </div>
      <div className="space-y-3">
        {topEarners.map((earner, index) => (
          <div key={earner.userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                index === 0 ? 'bg-yellow-500' : 
                index === 1 ? 'bg-gray-400' : 
                index === 2 ? 'bg-orange-500' : 'bg-blue-500'
              }`}>
                {index + 1}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{earner.userName}</p>
                <p className="text-xs text-gray-500">{earner.chatbots} chatbots</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900">
                {formatCurrency(earner.revenue)}
              </p>
              <p className="text-xs text-gray-500">Total earnings</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default AdminTopEarners 